
import { useState, useEffect } from 'react';

export default function Home() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Set target date to 7 days from now
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 7);

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/6a167064617e4a5d5efc75d1a853f690.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40"></div>
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>
            logo
          </h1>
        </div>

        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
            <i className="ri-tools-line text-3xl text-orange-600"></i>
          </div>

          {/* Title */}
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            We're Under Maintenance
          </h2>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Our website is currently undergoing scheduled maintenance to bring you exciting new features and improvements. 
            We'll be back online soon with a better experience than ever before.
          </p>

          {/* Countdown Timer */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Expected Return Time</h3>
            <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{timeLeft.days}</div>
                <div className="text-sm text-gray-600">Days</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{timeLeft.hours}</div>
                <div className="text-sm text-gray-600">Hours</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{timeLeft.minutes}</div>
                <div className="text-sm text-gray-600">Minutes</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{timeLeft.seconds}</div>
                <div className="text-sm text-gray-600">Seconds</div>
              </div>
            </div>
          </div>

          {/* What's Coming */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">What's Coming</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-speed-up-line text-xl text-blue-600"></i>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Faster Performance</h4>
                <p className="text-sm text-gray-600">Optimized loading speeds and enhanced user experience</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-palette-line text-xl text-green-600"></i>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Fresh Design</h4>
                <p className="text-sm text-gray-600">Modern interface with improved navigation and aesthetics</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center">
                  <i className="ri-star-line text-xl text-purple-600"></i>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">New Features</h4>
                <p className="text-sm text-gray-600">Exciting new functionality to enhance your experience</p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="border-t pt-6">
            <p className="text-gray-600 mb-4">Need immediate assistance?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="mailto:support@example.com" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                Email Support
              </a>
              <a 
                href="tel:+1234567890" 
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                Call Us
              </a>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-8">
          <p className="text-white/80 mb-4">Stay connected with us</p>
          <div className="flex justify-center gap-4">
            <a href="#" className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer">
              <i className="ri-facebook-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer">
              <i className="ri-twitter-x-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer">
              <i className="ri-instagram-fill"></i>
            </a>
            <a href="#" className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer">
              <i className="ri-linkedin-fill"></i>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-white/60 text-sm">
          <p>&copy; 2024 All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white/80 cursor-pointer">Website Builder</a></p>
        </div>
      </div>
    </div>
  );
}
